import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { AlertTriangle, DollarSign } from "lucide-react";

interface AssetAnalysisSectionProps {
  data: any;
}

export const AssetAnalysisSection = ({ data }: AssetAnalysisSectionProps) => {
  const criticalAssetsOutOfService = data?.criticalAssetsOutOfService;
  const costAnalysis = data?.unplannedMaintenanceCostAnalysis;
  const getMetricColor = (
    value: number,
    goodThreshold: number,
    poorThreshold: number,
    reverse = false
  ) => {
    if (reverse) {
      if (value <= goodThreshold) return "text-status-good";
      if (value <= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    } else {
      if (value >= goodThreshold) return "text-status-good";
      if (value >= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Critical Assets Out of Service
            </CardTitle>
            <CustomTooltip message="Assets currently offline that impact operations. Shows status, downtime duration, and estimated repair completion." />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {criticalAssetsOutOfService?.map((asset, index) => (
              <div
                key={index}
                className="p-3 rounded-lg border border-status-poor/20 bg-status-poor-bg"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-status-poor text-sm">
                      {asset.assetName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {asset.status} • Down: {asset.downtime}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium">
                      ETA: {asset.estimatedRepairTime ?? "NA"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Maintenance Cost Analysis
            </CardTitle>
            <CustomTooltip message="Breakdown of maintenance costs by asset type and failure severity distribution. Helps identify cost optimization opportunities." />
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm">Unplanned vs Planned:</span>
            <span
              className={`font-bold ${getMetricColor(
                costAnalysis?.ratio?.unplannedPercentage,
                20,
                30,
                true
              )}`}
            >
              {costAnalysis?.ratio?.unplannedPercentage}% /{" "}
              {costAnalysis?.ratio?.plannedPercentage}%
            </span>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Cost by Asset Type:</p>
            {costAnalysis?.costByType?.map((asset, index) => (
              <div
                key={index}
                className="flex justify-between items-center text-sm"
              >
                <span>{asset.type}:</span>
                <span className="font-medium">${asset.cost}</span>
              </div>
            ))}
          </div>

          <div className="border-t pt-3">
            <p className="text-sm font-medium mb-2">Failure Severity:</p>
            <div className="grid grid-cols-3 gap-2 text-xs text-center">
              <div className="text-status-poor">
                {costAnalysis?.failureSeverity?.critical}% Critical
              </div>
              <div className="text-status-fair">
                {costAnalysis?.failureSeverity?.major}% Major
              </div>
              <div className="text-status-good">
                {costAnalysis?.failureSeverity?.minor}% Minor
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
